// Centralized form styling utility with color types for consistent design

// Color palette with 6 different types
export const colorTypes = {
  type1: {
    primary: '#007bff',
    background: '#f8f9fa',
    border: '#007bff',
    shadow: '0 1px 3px rgba(0,123,255,0.1)',
  },
  type2: {
    primary: '#28a745',
    background: '#f8f9fa',
    border: '#28a745',
    shadow: '0 1px 3px rgba(40,167,69,0.1)',
  },
  type3: {
    primary: '#17a2b8',
    background: '#f8f9fa',
    border: '#17a2b8',
    shadow: '0 1px 3px rgba(23,162,184,0.1)',
  },
  type4: {
    primary: '#ffc107',
    background: '#f8f9fa',
    border: '#ffc107',
    shadow: '0 1px 3px rgba(255,193,7,0.1)',
  },
  type5: {
    primary: '#dc3545',
    background: '#f8f9fa',
    border: '#dc3545',
    shadow: '0 1px 3px rgba(220,53,69,0.1)',
  },
  type6: {
    primary: '#6f42c1',
    background: '#f8f9fa',
    border: '#6f42c1',
    shadow: '0 1px 3px rgba(111,66,193,0.1)',
  },
};

// Input field styles with type support
export const getInputFieldStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    backgroundColor: colors.background,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    boxShadow: colors.shadow,
    fontSize: '0.9rem',
    padding: '0.5rem 0.75rem',
    transition: 'all 0.3s ease',
  };
};

// Button styles with type support
export const getButtonStyle = (type = 'type1', variant = 'primary') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  const baseStyle = {
    borderRadius: '8px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    minHeight: '38px',
  };

  const variants = {
    primary: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary}dd 100%)`,
      color: '#ffffff',
      boxShadow: colors.shadow,
    },
    outline: {
      background: 'transparent',
      color: colors.primary,
      border: `2px solid ${colors.primary}`,
      boxShadow: 'none',
    },
    secondary: {
      background: colors.background,
      color: colors.primary,
      border: `1px solid ${colors.border}`,
      boxShadow: colors.shadow,
    },
  };

  return {
    ...baseStyle,
    ...variants[variant],
  };
};

// Select/Dropdown styles
export const getSelectStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    backgroundColor: colors.background,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    boxShadow: colors.shadow,
    fontSize: '0.9rem',
    padding: '0.5rem 0.75rem',
    transition: 'all 0.3s ease',
  };
};

// Card styles with type support
export const getCardStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    backgroundColor: '#ffffff',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    boxShadow: colors.shadow,
    overflow: 'hidden',
  };
};

// Form group styles
export const getFormGroupStyle = () => ({
  marginBottom: '1.5rem',
});

// Label styles with type support
export const getLabelStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    color: colors.primary,
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    display: 'block',
  };
};

// Textarea styles
export const getTextareaStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    backgroundColor: colors.background,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    boxShadow: colors.shadow,
    fontSize: '0.9rem',
    padding: '0.75rem',
    transition: 'all 0.3s ease',
    resize: 'vertical',
    minHeight: '100px',
  };
};

// Focus styles
export const getFocusStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    outline: 'none',
    borderColor: colors.primary,
    boxShadow: `0 0 0 0.2rem ${colors.primary}25`,
  };
};

// Hover styles
export const getHoverStyle = (type = 'type1') => {
  const colors = colorTypes[type] || colorTypes.type1;
  
  return {
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${colors.primary}30`,
  };
};

// Export default object with all utilities
export default {
  colorTypes,
  getInputFieldStyle,
  getButtonStyle,
  getSelectStyle,
  getCardStyle,
  getFormGroupStyle,
  getLabelStyle,
  getTextareaStyle,
  getFocusStyle,
  getHoverStyle,
};
